import {FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';
import {switchMap} from 'rxjs/internal/operators/switchMap';

import {
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    ClientUserTypesEnum,
    UsersService,
    CompanyEmployeesService,
    IndividualSubordinatesService,
    CompanyEmployeeResponseInterface,
    IndividualSubordinateResponseInterface,
    CountryModel,
    CompanyExtendedModel,
    IndividualExtendedModel,
    CompanyEmployeeInputModel,
    UserSendResetPasswordInputModel,
    IndividualSubordinateInputModel
} from '@spa/api';

import {PhoneValidator} from '@spa/validators';

import {PhoneAutocompleteValueInterface} from '../../../../shared/widgets';
import {BaseControl, Helper, PhoneAutocompleteControl} from '../../../../shared/classes';

import {AddUserToClientFormErrorStatesEnum} from '../../enums';

import {
    AddUserToClientControls,
    ClientUserTypeSelector,
    ClientUserPhoneSelector,
    ClientUserEmailSelector,
    ClientUserFirstNameSelector,
    ClientUserMiddleNameSelector,
    ClientUserLastNameSelector
} from '../../../../../assets/descriptors';

export class AddUserToClientForm {
    private readonly client: IndividualExtendedModel | CompanyExtendedModel;
    private readonly countries: CountryModel[];
    private readonly isCorporateClient: boolean;
    private readonly companyEmployeesService: CompanyEmployeesService;
    private readonly individualSubordinatesService: IndividualSubordinatesService;
    private readonly usersService: UsersService;

    public form: FormGroup;
    public controls: BaseControl<string | number | PhoneAutocompleteValueInterface>[];
    public controlsObservable: BehaviorSubject<BaseControl<string | number | PhoneAutocompleteValueInterface>[]>;

    constructor(
        client: IndividualExtendedModel | CompanyExtendedModel,
        countries: CountryModel[],
        isCorporateClient: boolean,
        companyEmployeesService: CompanyEmployeesService,
        individualSubordinatesService: IndividualSubordinatesService,
        usersService: UsersService
    ) {
        this.client = client;
        this.countries = countries;
        this.isCorporateClient = isCorporateClient;
        this.companyEmployeesService = companyEmployeesService;
        this.individualSubordinatesService = individualSubordinatesService;
        this.usersService = usersService;

        this.controlsObservable = new BehaviorSubject([]);
    }

    private mapRegistrationRequest(
        response: ServerResponseData<CompanyEmployeeResponseInterface | IndividualSubordinateResponseInterface>
    ): ServerResponse<boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponseObservable(
                response,
                AddUserToClientFormErrorStatesEnum.RegisterFailed
            );
        }

        const tenantId = Helper.enumsHelper().getDefaultTenantId();
        const portalId = this.client.portals[0].id;

        const sendResetPasswordEmail = new UserSendResetPasswordInputModel(
            this.form.value[ClientUserEmailSelector],
            Helper.enumsHelper().getClientNameByPortalId(tenantId, portalId),
            Helper.enumsHelper().getLoginUrlByPortalId(tenantId, portalId)
        );

        return this.usersService.sendResetPassword(sendResetPasswordEmail).pipe(
            map(() => {
                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    initForm(): void {
        this.form = new FormGroup({});

        this.controls = AddUserToClientControls().filter((control: BaseControl<string | number | PhoneAutocompleteValueInterface>) => {
            return !(control.name === ClientUserTypeSelector && !this.isCorporateClient);
        }).map((control: BaseControl<string | number | PhoneAutocompleteValueInterface>) => {
            if (control.name === ClientUserPhoneSelector) {
                const countryIds = this.countries.map((country: CountryModel) => country.phoneCode);

                (control as PhoneAutocompleteControl).countries = this.countries;
                (control as PhoneAutocompleteControl).extendValidators(PhoneValidator(countryIds));
            }

            this.form.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.controlsObservable.next(this.controls);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            this.controls.map((control: BaseControl) => {
                if (!this.form.controls[control.name]) {
                    return false;
                }

                const formControl = this.form.controls[control.name];

                if (formControl.invalid) {
                    const errorKeys = Object.keys(formControl.errors);

                    const errors = errorKeys.map((key: string) => {
                        return messageFunction(control.placeholder)[key];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(): ServerResponse<boolean> {
        const code = (this.form.value[ClientUserPhoneSelector] as PhoneAutocompleteValueInterface).code;
        const phone = (this.form.value[ClientUserPhoneSelector] as PhoneAutocompleteValueInterface).phone;
        const phoneNumber = code + phone;

        if (this.isCorporateClient) {
            const companyEmployeeInputModel = new CompanyEmployeeInputModel(
                this.client.id,
                this.form.value[ClientUserFirstNameSelector],
                this.form.value[ClientUserMiddleNameSelector],
                this.form.value[ClientUserLastNameSelector],
                this.form.value[ClientUserEmailSelector],
                this.form.value[ClientUserTypeSelector] === ClientUserTypesEnum.LegalRepresentative,
                phoneNumber,
                Helper.enumsHelper().getConfidentialClientByTypeBehavior(this.client.typeBehaviour)
            );

            return this.companyEmployeesService.registerCompanyEmployee(companyEmployeeInputModel).pipe(
                switchMap((response: ServerResponseData<CompanyEmployeeResponseInterface>) => {
                    return this.mapRegistrationRequest(response);
                })
            );
        }

        const individualSubordinateInputModel = new IndividualSubordinateInputModel(
            this.form.value[ClientUserFirstNameSelector],
            this.form.value[ClientUserMiddleNameSelector],
            this.form.value[ClientUserLastNameSelector],
            this.form.value[ClientUserEmailSelector],
            phoneNumber,
            this.client.id,
            Helper.enumsHelper().getConfidentialClientByTypeBehavior(this.client.typeBehaviour)
        );

        return this.individualSubordinatesService.registerIndividualSubordinate(individualSubordinateInputModel).pipe(
            switchMap((response: ServerResponseData<IndividualSubordinateResponseInterface>) => {
                return this.mapRegistrationRequest(response);
            })
        );
    }
}
